import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Container, Form, Modal, Spinner, Table } from 'react-bootstrap'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEMO_STATUS, UPLOAD_BASE, fmtDateTimeTH } from './aoShared'

// ผู้บริหาร (pos 90): แฟ้มเสนอพิจารณา — อนุมัติ/สั่งการ, ตีกลับ, ไม่อนุมัติ
export default function ExecMemoInbox() {
  const [status, setStatus] = useState('proposed')
  const [memos, setMemos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMemo, setViewMemo] = useState(null)
  const [comment, setComment] = useState('')
  const [deciding, setDeciding] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      const res = await axiosInstance.get(`/api/ao/exec/memos?${params.toString()}`)
      if (res.data.success) setMemos(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { fetchData() }, [fetchData])

  const openMemo = (m) => {
    setViewMemo(m)
    setComment('')
  }

  const handleDecide = async (decision, label) => {
    if (!viewMemo) return
    if (decision !== 'approved' && !comment.trim()) {
      Swal.fire({ icon: 'warning', title: `กรุณาระบุเหตุผลในการ${label}` })
      return
    }
    setDeciding(true)
    try {
      await axiosInstance.put(`/api/ao/exec/memos/${viewMemo.mm_id}/decision`, { decision, comment })
      setViewMemo(null)
      Swal.fire({ icon: 'success', title: `${label}เรียบร้อย`, timer: 1800, showConfirmButton: false })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: `${label}ไม่สำเร็จ`, text: err.response?.data?.message || '' })
    } finally {
      setDeciding(false)
    }
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-folder-shield-2-line me-2 text-primary" />
              แฟ้มเสนอพิจารณา
            </h4>
            <Form.Select size="sm" style={{ width: 170 }} value={status} onChange={e => setStatus(e.target.value)}>
              <option value="proposed">รอพิจารณา</option>
              <option value="approved">อนุมัติ/สั่งการแล้ว</option>
              <option value="returned">ตีกลับ</option>
              <option value="rejected">ไม่อนุมัติ</option>
              <option value="">ทั้งหมด</option>
            </Form.Select>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <Card className="card-one">
              <Card.Body className="p-0">
                {memos.length === 0 ? (
                  <div className="text-center text-muted py-4">ไม่มีแฟ้มในเงื่อนไขที่เลือก</div>
                ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 90 }}>เลขบันทึก</th>
                        <th>เรื่อง</th>
                        <th style={{ width: 150 }}>ผู้เขียน</th>
                        <th style={{ width: 140 }}>วันที่เสนอ</th>
                        <th style={{ width: 130 }}>สถานะ</th>
                        <th style={{ width: 110 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {memos.map(m => {
                        const st = MEMO_STATUS[m.mm_status] || MEMO_STATUS.proposed
                        return (
                          <tr key={m.mm_id}>
                            <td>{m.mm_full_no || '-'}</td>
                            <td>
                              <div className="fw-semibold">{m.mm_subject}</div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>{m.mm_category}</div>
                            </td>
                            <td>{m.created_by_name}</td>
                            <td>{fmtDateTimeTH(m.mm_proposed_at)}</td>
                            <td><Badge bg={st.bg}>{st.label}</Badge></td>
                            <td className="text-end">
                              <Button size="sm" variant={m.mm_status === 'proposed' ? 'primary' : 'outline-secondary'}
                                onClick={() => openMemo(m)}>
                                {m.mm_status === 'proposed' ? 'พิจารณา' : 'ดู'}
                              </Button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          )}
        </Container>
      </div>
      <Footer />

      {/* Decision modal */}
      <Modal show={!!viewMemo} onHide={() => setViewMemo(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {viewMemo?.mm_category} {viewMemo?.mm_full_no ? `— ${viewMemo.mm_full_no}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewMemo && (
            <>
              <div className="fw-semibold mb-1">เรื่อง: {viewMemo.mm_subject}</div>
              <div className="text-muted fs-sm mb-2">
                ผู้เขียน: {viewMemo.created_by_name} · เสนอโดย: {viewMemo.proposed_by_name || '-'} · {fmtDateTimeTH(viewMemo.mm_proposed_at)}
              </div>
              {viewMemo.mm_body ? (
                <div className="border rounded p-3 mb-2" dangerouslySetInnerHTML={{ __html: viewMemo.mm_body }} />
              ) : (
                <div className="text-muted fs-sm mb-2">— ไม่มีเนื้อหา —</div>
              )}
              {viewMemo.mm_file && (
                <div className="mb-2">
                  <a href={`${UPLOAD_BASE}/uploads/ao_docs/${viewMemo.mm_file}`} target="_blank" rel="noreferrer">
                    <i className="ri-attachment-2 me-1" />เปิดไฟล์แนบ
                  </a>
                </div>
              )}
              {viewMemo.mm_status === 'proposed' ? (
                <Form.Group>
                  <Form.Label className="fs-sm mb-1">คำสั่งการ / ความเห็น</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={3} value={comment}
                    placeholder="เช่น อนุมัติ ดำเนินการตามเสนอ / มอบฝ่าย... ดำเนินการ"
                    onChange={e => setComment(e.target.value)} />
                </Form.Group>
              ) : viewMemo.mm_exec_comment && (
                <Alert variant="light" className="border fs-sm py-2 mb-0">
                  <span className="text-muted">คำสั่งการ:</span> {viewMemo.mm_exec_comment}
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    โดย {viewMemo.exec_by_name} · {fmtDateTimeTH(viewMemo.mm_exec_at)}
                  </div>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        {viewMemo?.mm_status === 'proposed' && (
          <Modal.Footer>
            <Button variant="outline-danger" size="sm" disabled={deciding} onClick={() => handleDecide('rejected', 'ไม่อนุมัติ')}>
              ไม่อนุมัติ
            </Button>
            <Button variant="outline-warning" size="sm" disabled={deciding} onClick={() => handleDecide('returned', 'ตีกลับให้แก้ไข')}>
              ตีกลับให้แก้ไข
            </Button>
            <Button variant="success" size="sm" disabled={deciding} onClick={() => handleDecide('approved', 'อนุมัติ/สั่งการ')}>
              {deciding ? <Spinner size="sm" className="me-1" /> : <i className="ri-check-line me-1" />}
              อนุมัติ / สั่งการ
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  )
}
